import { featureCards, comparisonData } from "@/mocks/basetaoData";

export default function ContentSections() {
  return (
    <>
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-900 mb-6 text-center">
              What Is BaseTao Spreadsheet?
            </h2>
            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
              <p className="mb-4">
                <strong>BaseTao Spreadsheet</strong> is a powerful resource system designed to help users organize product research, compare options, track costs, and make better purchasing decisions. Instead of searching thousands of products manually, spreadsheet-based research helps buyers analyze critical shopping data in one centralized view.
              </p>
              <p className="mb-4">
                Whether you are a first-time buyer exploring international shopping platforms or an experienced shopper looking to optimize your workflow, BaseTao Spreadsheet provides the structured framework you need. The system covers product links, price comparisons, seller information, shipping estimates, warehouse updates, and purchase history &mdash; all organized for quick reference.
              </p>
              <ul className="space-y-2 list-disc pl-5">
                <li>Product links organized by category and seller rating</li>
                <li>Price comparisons across different time periods and vendors</li>
                <li>Seller information with reliability indicators</li>
                <li>Shipping estimates broken down by destination and method</li>
                <li>Warehouse updates with processing status tracking</li>
                <li>Purchase history for informed repeat buying decisions</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 bg-background-50" id="shipping">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-900 mb-3 text-center">
              Why Use BaseTao Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-xl mx-auto">
              See how BaseTao Spreadsheet transforms your shopping research compared to traditional manual browsing.
            </p>

            <div className="overflow-x-auto rounded-xl border border-background-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-5 py-4 font-semibold text-foreground-800 whitespace-nowrap">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-foreground-600 whitespace-nowrap">Traditional Shopping</th>
                    <th className="text-left px-5 py-4 font-semibold text-primary-600 whitespace-nowrap">BaseTao Spreadsheet</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, idx) => (
                    <tr key={row.feature} className={idx % 2 === 0 ? "bg-background-50" : "bg-background-100"}>
                      <td className="px-5 py-3.5 font-medium text-foreground-800 whitespace-nowrap">{row.feature}</td>
                      <td className="px-5 py-3.5 text-foreground-500 whitespace-nowrap">{row.traditional}</td>
                      <td className="px-5 py-3.5 text-primary-600 font-medium whitespace-nowrap">{row.spreadsheet}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground-900 mb-3 text-center">
              BaseTao Spreadsheet Features
            </h2>
            <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-xl mx-auto">
              Everything you need to make informed purchasing decisions, all in one organized system.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featureCards.map((card) => (
                <div
                  key={card.title}
                  className="group p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-accent-300 transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4 group-hover:bg-accent-200 transition-colors duration-200">
                    <i className={`${card.icon} text-xl text-accent-600 w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <h4 className="font-heading font-semibold text-foreground-900 mb-2 text-base">{card.title}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}